import * as React from 'react';

import { getLocale } from '@rematter/common/locale';
import { enumUnit } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/brokerage/i18n/useTranslations';
import {
  cn,
  FooterItem,
  humanReadablePercent,
  MeasuredValue,
  Money,
} from '@rematter/ui-web';

import { useBrokeredOrderLineItemsTotals } from '~/modules/brokerage/hooks/useBrokeredOrderLineItemsTotals';
import { ItemMarginRecord } from '~/modules/brokerage/types/brokeredOrder';
import { BrokeredOrderLineItemRecord } from '~/modules/brokerage/types/brokeredOrderLineItem';

type Props = {
  lineItems: BrokeredOrderLineItemRecord[];
  marginsData?: ItemMarginRecord[];
};

export const BrokeredOrderLineItemsSummaryFooter: React.FC<Props> = ({
  lineItems,
  marginsData,
}) => {
  const t = useTranslations();

  const {
    totalWeight,
    totalEachCount,
    purchaseTotal,
    salesTotal,
    margin,
    marginPercent,
  } = useBrokeredOrderLineItemsTotals(lineItems, marginsData);

  const isNegativeMargin = margin && margin.amount < 0;
  const hasWeight = totalWeight !== null && (totalWeight?.value ?? 0) > 0;
  const hasEachCount = totalEachCount > 0;

  return (
    <>
      {lineItems.length > 0 && (
        <>
          <FooterItem label={t.orders.materials.totals.material}>
            <span className="text-lg font-bold tracking-tight">
              {lineItems.length}
            </span>
          </FooterItem>
          <div className="h-9 w-px bg-neutral/10" />
        </>
      )}

      <FooterItem label={t.orders.materials.totals.totalWeight}>
        <div className="flex items-baseline gap-3 text-lg font-semibold">
          {hasWeight && (
            <div className="text-lg font-bold">
              <MeasuredValue
                value={totalWeight.value}
                unit={totalWeight.unit}
              />
            </div>
          )}
          {hasEachCount && (
            <div className="text-lg font-bold">
              <MeasuredValue value={totalEachCount} unit={enumUnit.Each} />
            </div>
          )}
        </div>
      </FooterItem>

      <div className="h-9 w-px bg-neutral/10" />

      <FooterItem label={t.orders.materials.totals.totalBuy}>
        <div className="text-lg font-semibold">
          <Money {...purchaseTotal} locale={getLocale()} />
        </div>
      </FooterItem>

      <div className="h-9 w-px bg-neutral/10" />

      <FooterItem label={t.orders.materials.totals.totalSell}>
        <div className="text-lg font-semibold">
          <Money {...salesTotal} locale={getLocale()} />
        </div>
      </FooterItem>

      <div className="h-9 w-px bg-neutral/10" />

      <FooterItem label={t.orders.materials.totals.totalMargin}>
        <div className="flex flex-col items-end">
          <div
            className={cn(
              'flex justify-between text-lg font-semibold',
              isNegativeMargin && 'text-failure',
            )}
          >
            <Money {...margin} locale={getLocale()} />
          </div>
          {!!marginPercent && (
            <span
              className={cn(
                'text-xs font-normal text-neutral/60',
                isNegativeMargin && 'text-failure',
              )}
            >
              {humanReadablePercent(marginPercent, { locale: getLocale() })}
            </span>
          )}
        </div>
      </FooterItem>
    </>
  );
};
